
import { GeneratorConfig, TextType, PageStructure, Theme } from "../types";

// Thematic Word Banks - 100% Local
const WORD_BANKS: Record<Theme, string[]> = {
  [Theme.LATIN]: ["lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit", "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore", "magna", "aliqua"],
  [Theme.CORPORATE]: ["synergy", "paradigm", "holistic", "vertical", "leverage", "bandwidth", "scalability", "deliverable", "ecosystem", "agile", "roi", "transformation", "alignment", "stakeholder", "workflow"],
  [Theme.CYBERPUNK]: ["neon", "glitch", "mainframe", "neural", "hacker", "chrome", "cybernetic", "dystopia", "matrix", "synthwave", "interface", "protocol", "data", "encryption", "algorithm", "cyborg"],
  [Theme.PIRATE]: ["ahoy", "matey", "scallywag", "booty", "doubloons", "galleon", "walk", "plank", "jolly", "roger", "landlubber", "treasure", "ship", "captain", "crew", "ocean", "rum"],
  [Theme.FOODIE]: ["artisanal", "organic", "infused", "deconstructed", "umami", "gourmet", "savory", "farm-to-table", "rustic", "reduction", "seasoned", "culinary", "palette", "delicacy"],
  [Theme.HIPSTER]: ["bespoke", "vinyl", "kombucha", "ironic", "selvedge", "craft", "mustache", "portland", "fixie", "pabst", "vintage", "authentic", "minimalist", "sustainable", "handcrafted"],
  [Theme.LEGAL]: ["aforementioned", "indemnification", "jurisdiction", "statute", "affidavit", "plaintiff", "defendant", "litigation", "contract", "clauses", "notary", "hereby", "binding", "witness"],
  [Theme.SAAS]: ["onboarding", "churn", "retention", "conversion", "automation", "dashboard", "metrics", "seamless", "cloud-native", "infrastructure", "enterprise", "integration", "workflow", "subscription"]
};

const getRandom = (arr: any[]) => arr[Math.floor(Math.random() * arr.length)];

const generateSentence = (theme: Theme, custom: string[], minWords = 5, maxWords = 12) => {
  const bank = [...WORD_BANKS[theme], ...custom];
  const length = Math.floor(Math.random() * (maxWords - minWords)) + minWords;
  const words = Array.from({ length }, () => getRandom(bank));
  const sentence = words.join(" ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1) + ".";
};

const generateParagraph = (theme: Theme, custom: string[], count = 3) => {
  return Array.from({ length: count }, () => generateSentence(theme, custom, 8, 15)).join(" ");
};

/**
 * Generates thematic text locally using word banks.
 * No API key required.
 */
export const generateIpsum = async (config: GeneratorConfig): Promise<string | PageStructure> => {
  const { type, count, theme, startWithLorem, customWords, includeImages } = config;
  const customList = customWords.split(",").map(w => w.trim()).filter(Boolean);

  // Small delay to simulate "processing" for better UX
  await new Promise(r => setTimeout(r, 300));

  if (type === TextType.PAGE_LAYOUT) {
    const sections = Array.from({ length: count }, (_, i) => {
      const headingWords = generateSentence(theme, customList, 2, 4).replace(".", "");
      const keyword = headingWords.split(" ")[0].toLowerCase();
      return {
        heading: headingWords,
        content: [generateParagraph(theme, customList, 2), generateParagraph(theme, customList, 1)],
        bullets: Array.from({ length: 3 }, () => generateSentence(theme, customList, 4, 8)),
        // Placeholder images using a public stable service
        imageUrl: includeImages ? `https://loremflickr.com/800/450/${theme.toLowerCase().replace(/[^a-z]/g, '')},${keyword}?lock=${i}` : undefined
      };
    });

    return {
      title: (startWithLorem && theme === Theme.LATIN ? "Lorem Ipsum " : "") + generateSentence(theme, customList, 2, 3).replace(".", ""),
      heroSubtitle: generateSentence(theme, customList, 6, 10),
      sections
    } as PageStructure;
  }

  let text = "";
  if (type === TextType.WORDS) {
    const bank = [...WORD_BANKS[theme], ...customList];
    text = Array.from({ length: count }, () => getRandom(bank)).join(" ");
  } else if (type === TextType.SENTENCES) {
    text = Array.from({ length: count }, () => generateSentence(theme, customList)).join(" ");
  } else {
    text = Array.from({ length: count }, () => generateParagraph(theme, customList)).join("\n\n");
  }

  if (startWithLorem && theme === Theme.LATIN) {
    text = "Lorem ipsum dolor sit amet, " + text.charAt(0).toLowerCase() + text.slice(1);
  }

  return text;
};
